/**
 * #191 quick-batch — interview-depth toggle for the Dashboard's
 * "Create New Game" dialog.
 *
 * Visible segmented control: `'quick'` (default) runs the Fast
 * questions + plan-mode flow; `'just-build'` (#1773) skips the
 * interview entirely (zero questions — the agent makes sensible
 * default choices and records them); `'advanced'` opens the Design
 * tab authoring path. The legacy `'full'` depth remains in the schema
 * for direct callers and existing games, but product UI no longer
 * offers it.
 *
 * Design choices baked into this component (per the #191 spec):
 *
 *   - Toggle defaults to `'quick'` on every dialog open. No
 *     per-user memory, no settings preference. The parent owns
 *     the state — a freshly mounted toggle defers to the parent's
 *     initial `value` prop, which the Dashboard always sets to
 *     `'quick'` when opening the dialog.
 *   - Visually inline above the Create button. Extracted to its own
 *     component (instead of inlining the markup in `Dashboard.tsx`)
 *     so the parallel description-validation PR (#538) and this PR
 *     don't both touch the same Dashboard region.
 *   - Pure controlled component — no internal state. Tests can
 *     pass `value` + `onChange` and pin all behaviour without
 *     rendering the parent dialog.
 *
 * #1773 note: `'just-build'` is a KICKOFF mode, not a persisted
 * interview depth — the Dashboard maps it to `interviewDepth: 'quick'`
 * + `interviewBestEffort: true` on the `game:create` payload, so the
 * persisted depth taxonomy (`KnownInterviewDepth`) stays untouched.
 */

import type { ReactElement } from 'react';
import type { KnownInterviewDepth } from '@volley/crucible-ipc-types/build-game-types';

/**
 * Alias kept for the toggle's external API — semantically identical
 * to the canonical `KnownInterviewDepth` from schema PR #543. Lets
 * `Dashboard.tsx` import the type from this file without taking a
 * dep on the shared schema module just to declare a local state
 * variable.
 */
export type CreateGameInterviewDepth = KnownInterviewDepth;

/**
 * #1773 — the segmented control's full value space: the persisted
 * depths plus the renderer-only `'just-build'` kickoff mode (mapped to
 * quick + `interviewBestEffort: true` at the `game:create` call site;
 * never written to the state file as a depth).
 */
export type CreateGameKickoffMode = CreateGameInterviewDepth | 'just-build';

export interface CreateGameInterviewDepthToggleProps {
  /** Currently selected kickoff mode. Parent owns the state. */
  value: CreateGameKickoffMode;
  /** Fires whenever the user clicks an inactive option. */
  onChange: (next: CreateGameKickoffMode) => void;
  /** Whether the "Advanced" (Design-tab) option is offered. Defaults to
   *  true; the parent passes `flags.designTab` so the option disappears
   *  when the Design-tab feature flag is off (`CRUCIBLE_DESIGN_TAB=0`). */
  allowAdvanced?: boolean;
}

/** Test-id constants — exported so e2e + SSR tests can pin them. */
export const DEPTH_TOGGLE_TESTID = 'interview-depth-toggle';
export const DEPTH_TOGGLE_QUICK_TESTID = 'interview-depth-toggle-quick';
/** Legacy test id kept exported for downstream imports; no longer rendered. */
export const DEPTH_TOGGLE_FULL_TESTID = 'interview-depth-toggle-full';
export const DEPTH_TOGGLE_ADVANCED_TESTID = 'interview-depth-toggle-advanced';
/** #1773 — the zero-questions "Just build" option. */
export const DEPTH_TOGGLE_JUST_BUILD_TESTID = 'interview-depth-toggle-just-build';

/**
 * Segmented-control rendering. The active option is highlighted with
 * the `bg-crucible-accent` brand colour the rest of the dialog uses
 * for its primary CTA; the inactive options fade to white/40 so the
 * default pick is visually obvious before the user reads the labels.
 *
 * All buttons render even when `value` doesn't match — the inactive
 * ones are the click targets that fire `onChange`.
 */
export function CreateGameInterviewDepthToggle({
  value,
  onChange,
  allowAdvanced = true,
}: CreateGameInterviewDepthToggleProps): ReactElement {
  const effectiveValue =
    value === 'advanced' && !allowAdvanced ? 'quick' : value === 'full' ? 'quick' : value;
  const quickActive = effectiveValue === 'quick';
  const justBuildActive = effectiveValue === 'just-build';
  const advancedActive = effectiveValue === 'advanced';

  return (
    <div className="mb-4" data-testid={DEPTH_TOGGLE_TESTID} data-value={effectiveValue}>
      <label className="mb-1 block text-sm text-white/60">Kickoff mode</label>
      <div
        role="radiogroup"
        aria-label="Kickoff mode"
        className="inline-flex rounded-lg border border-white/10 bg-white/5 p-1 text-sm"
      >
        <button
          type="button"
          role="radio"
          aria-checked={quickActive}
          data-testid={DEPTH_TOGGLE_QUICK_TESTID}
          data-active={quickActive ? 'true' : 'false'}
          onClick={() => {
            if (!quickActive) onChange('quick');
          }}
          className={
            'rounded-md px-3 py-1.5 transition-colors ' +
            (quickActive
              ? 'bg-crucible-accent text-white shadow-sm'
              : 'text-white/40 hover:text-white/70')
          }
        >
          Fast
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={justBuildActive}
          data-testid={DEPTH_TOGGLE_JUST_BUILD_TESTID}
          data-active={justBuildActive ? 'true' : 'false'}
          onClick={() => {
            if (!justBuildActive) onChange('just-build');
          }}
          className={
            'ml-1 rounded-md px-3 py-1.5 transition-colors ' +
            (justBuildActive
              ? 'bg-crucible-accent text-white shadow-sm'
              : 'text-white/40 hover:text-white/70')
          }
        >
          Just build
        </button>
        {allowAdvanced && (
          <button
            type="button"
            role="radio"
            aria-checked={advancedActive}
            data-testid={DEPTH_TOGGLE_ADVANCED_TESTID}
            data-active={advancedActive ? 'true' : 'false'}
            onClick={() => {
              if (!advancedActive) onChange('advanced');
            }}
            className={
              'ml-1 rounded-md px-3 py-1.5 transition-colors ' +
              (advancedActive
                ? 'bg-crucible-accent text-white shadow-sm'
                : 'text-white/40 hover:text-white/70')
            }
          >
            Advanced
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-white/40">
        {quickActive && 'Five fast questions, then review a plan before building.'}
        {justBuildActive &&
          'No questions — the agent makes sensible choices and starts building immediately.'}
        {advancedActive && 'Author a full design doc in the Design tab — replaces the interview.'}
      </p>
    </div>
  );
}
